import React from 'react';
import { RouteInfo, RouteDetails, AlternativeRoute } from '../../types/fare';
import { formatDuration, formatDistance, formatSavings } from '../../utils/fareCalculationUtils';
import { Navigation, Clock, MapPin } from 'lucide-react';

interface RouteInfoSectionProps {
  routeInfo: RouteInfo;
  selectedRouteIndex: number;
  onRouteSelect: (index: number) => void;
  weatherMultiplier?: number;
}

export default function RouteInfoSection({
  routeInfo,
  selectedRouteIndex,
  onRouteSelect,
}: RouteInfoSectionProps) {
  const { recommended, alternatives } = routeInfo;

  // Combine recommended + alternatives for unified display
  const allRoutes: (RouteDetails | AlternativeRoute)[] = [recommended, ...alternatives];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <Navigation size={18} className="text-gray-600" />
        <h3 className="font-semibold text-gray-900">Route Options</h3>
        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
          {routeInfo.totalRoutesFound} {routeInfo.totalRoutesFound === 1 ? 'route' : 'routes'}
        </span>
      </div>

      {allRoutes.map((route, index) => {
        const isRecommended = index === 0;
        const isSelected = selectedRouteIndex === index;
        const isAlternative = index > 0;

        return (
          <button
            key={index}
            onClick={() => onRouteSelect(index)}
            className={`w-full text-left rounded-lg p-4 border-2 transition-all ${
              isSelected
                ? isRecommended
                  ? 'bg-green-50 border-green-500'
                  : 'bg-blue-50 border-blue-500'
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {isRecommended && (
                    <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-semibold">
                      Recommended
                    </span>
                  )}
                  {isAlternative && (
                    <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded-full">
                      Alternative {index}
                    </span>
                  )}
                </div>
                <div className="font-semibold text-gray-900">{route.summary}</div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
              <div className="flex items-center gap-1">
                <MapPin size={14} />
                <span>{formatDistance(route.distanceKm)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>{formatDuration(route.durationMins)}</span>
              </div>
            </div>

            {/* Per-route fare display */}
            {route.fare && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                {route.fare.mode && route.fare.min && route.fare.max ? (
                  <div className="text-sm font-semibold text-gray-900">
                    💰 Rs. {route.fare.min.toLocaleString()} - {route.fare.max.toLocaleString()}
                    {route.fare.ceiling && (
                      <span className="text-xs font-normal text-gray-600 ml-2">
                        (ceiling: Rs. {route.fare.ceiling.toLocaleString()})
                      </span>
                    )}
                  </div>
                ) : route.fare.modes ? (
                  <div className="text-xs space-y-1">
                    <div className="text-gray-900">
                      🛺 Tuk: Rs. {route.fare.modes.tuk.min.toLocaleString()} - {route.fare.modes.tuk.max.toLocaleString()}
                    </div>
                    <div className="text-gray-900">
                      🚖 Taxi: Rs. {route.fare.modes.taxi.min.toLocaleString()} - {route.fare.modes.taxi.max.toLocaleString()}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Savings comparison for alternatives */}
            {isAlternative && 'savings' in route && route.savings && (
              <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
                vs Recommended: {formatSavings(
                  (route as AlternativeRoute).savings.distanceKm, 
                  (route as AlternativeRoute).savings.timeMins
                )}
              </div>
            )}

            {/* Reasoning for recommended route */}
            {isRecommended && route.reasoning && (
              <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
                {route.reasoning}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

